/**
 * Discounts & Promotions Page
 * 
 * Manage promotional content for website display AND Square POS discount codes:
 * - Website Display: Banners, popups, homepage sections
 * - Square Discount Codes: Coupon codes that sync to Square POS
 */

import { useState, useEffect } from 'react';
import { formatAnyDateDisplay, localYyyyMmDd } from '../../utils/calendarDate';
import { 
  HiPlus, 
  HiPencil, 
  HiTrash, 
  HiRefresh,
  HiX,
  HiCheck,
  HiSpeakerphone,
  HiTag,
  HiSparkles,
  HiViewGrid,
  HiGlobe,
  HiCreditCard,
  HiDownload,
  HiCheckCircle
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// Promo type with Square sync field
interface Promo {
  id: string;
  title: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed' | 'info' | null;
  discount_value: number | null;
  promo_code: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  banner_image_url: string | null;
  created_at: string;
  // Coupon tracking fields
  target_audience: string | null;
  free_item: string | null;
  max_total_uses: number | null;
  max_uses_per_customer: number | null;
  current_uses: number;
  requires_verification: boolean;
  // Display type fields (can be comma-separated string: "banner,section")
  display_type: string | null;
  is_daily_special: boolean;
  priority: number;
  /** Optional site path for banner/section CTA, e.g. /menu/thanksgiving-2026 */
  link_path: string | null;
  // Square sync field
  square_discount_id: string | null;
}

// Form state
interface PromoForm {
  title: string;
  description: string;
  discount_type: string;
  discount_value: string;
  promo_code: string;
  start_date: string;
  end_date: string;
  banner_image_url: string;
  // Coupon tracking fields
  target_audience: string;
  free_item: string;
  max_total_uses: string;
  max_uses_per_customer: string;
  requires_verification: boolean;
  // Display type fields (stored as comma-separated string, but handled as array in form)
  display_type: string; // Comma-separated: "banner,section" or single: "banner"
  is_daily_special: boolean;
  priority: string;
  link_path: string;
}

const emptyForm: PromoForm = {
  title: '',
  description: '',
  discount_type: 'info',
  discount_value: '',
  promo_code: '',
  start_date: '',
  end_date: '',
  banner_image_url: '',
  target_audience: '',
  free_item: '',
  max_total_uses: '',
  max_uses_per_customer: '1',
  requires_verification: true,
  display_type: '',
  is_daily_special: false,
  priority: '100',
  link_path: '',
};

const getTodayDate = () => localYyyyMmDd();

export default function Promos() {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingFromSquare, setSyncingFromSquare] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PromoForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  // Helper functions for display_type (comma-separated string <-> array)
  const displayTypeToArray = (displayType: string | null): string[] => {
    if (!displayType) return [];
    return displayType.split(',').map(t => t.trim()).filter(t => t);
  };

  const arrayToDisplayType = (types: string[]): string => {
    return types.filter(t => t).join(',');
  };

  const toggleDisplayType = (type: string) => {
    const currentTypes = displayTypeToArray(form.display_type);
    const index = currentTypes.indexOf(type);
    if (index >= 0) {
      currentTypes.splice(index, 1);
    } else {
      currentTypes.push(type);
    }
    setForm({ ...form, display_type: arrayToDisplayType(currentTypes) });
  };

  // Fetch promos
  const fetchPromos = async () => {
    setLoading(true);
    try {
      const response = await adminFetch('/api/promos');
      if (!response.ok) throw new Error('Failed to fetch promos');
      const data = await response.json();
      setPromos(data.promos || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load promos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  // Sync discounts FROM Square
  const syncFromSquare = async () => {
    setSyncingFromSquare(true);
    try {
      const response = await adminFetch('/api/promos?syncSquare=true');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to sync from Square');
      }
      const data = await response.json();
      setPromos(data.promos || []);
      alert(`✅ Imported ${data.syncResults?.added || 0} discount(s) from Square (nothing was changed in Square)`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync from Square';
      alert(`❌ ${message}\n\nNote: Import requires Square DISCOUNTS_READ scope. Contact support if this keeps happening.`);
    } finally {
      setSyncingFromSquare(false);
    }
  };

  // Open modal for new promo
  const openNewModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  // Quick create daily special
  const openDailySpecialModal = () => {
    const today = getTodayDate();
    setForm({
      ...emptyForm,
      title: 'Today\'s Special: ',
      is_daily_special: true,
      display_type: 'popup', // Can be changed to multiple in the form
      start_date: today,
      end_date: today,
      priority: '1',
    });
    setEditingId(null);
    setShowModal(true);
  };

  // Quick create announcement banner
  const openAnnouncementModal = () => {
    setForm({
      ...emptyForm,
      display_type: 'banner', // Can be changed to multiple in the form
      priority: '1',
    });
    setEditingId(null);
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (promo: Promo) => {
    setForm({
      title: promo.title,
      description: promo.description || '',
      discount_type: promo.discount_type || 'info',
      discount_value: promo.discount_value?.toString() || '',
      promo_code: promo.promo_code || '',
      start_date: promo.start_date?.split('T')[0] || '',
      end_date: promo.end_date?.split('T')[0] || '',
      banner_image_url: promo.banner_image_url || '',
      target_audience: promo.target_audience || '',
      free_item: promo.free_item || '',
      max_total_uses: promo.max_total_uses?.toString() || '',
      max_uses_per_customer: promo.max_uses_per_customer?.toString() || '1',
      requires_verification: promo.requires_verification !== false,
      display_type: promo.display_type || '',
      is_daily_special: promo.is_daily_special || false,
      priority: promo.priority?.toString() || '100',
      link_path: promo.link_path || '',
    });
    setEditingId(promo.id);
    setShowModal(true);
  };

  // Save promo
  const handleSave = async () => {
    if (!form.title.trim()) {
      alert('Title is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingId ? `/api/promos/${editingId}` : '/api/promos';
      const method = editingId ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description.trim() || null,
          discount_type: form.discount_type,
          discount_value: form.discount_value ? parseFloat(form.discount_value) : null,
          promo_code: form.promo_code.trim() || null,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          banner_image_url: form.banner_image_url.trim() || null,
          target_audience: form.target_audience.trim() || null,
          free_item: form.free_item.trim() || null,
          max_total_uses: form.max_total_uses ? parseInt(form.max_total_uses) : null,
          max_uses_per_customer: form.max_uses_per_customer ? parseInt(form.max_uses_per_customer) : 1,
          requires_verification: form.requires_verification,
          display_type: form.display_type || null,
          is_daily_special: form.is_daily_special,
          priority: form.priority ? parseInt(form.priority) : 100,
          link_path: form.link_path.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save');
      }

      setShowModal(false);
      fetchPromos();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save promo';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const toggleActive = async (id: string, currentState: boolean) => {
    setPromos(promos.map(p => 
      p.id === id ? { ...p, is_active: !currentState } : p
    ));

    try {
      const response = await adminFetch(`/api/promos/${id}/toggle-active`, {
        method: 'PUT',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setPromos(promos.map(p => 
          p.id === id ? { ...p, is_active: currentState } : p
        ));
        throw new Error(errorData.error || 'Failed to update');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update promo';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete promo
  const deletePromo = async (id: string, title: string) => {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;

    try {
      const response = await adminFetch(`/api/promos/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete');
      }
      setPromos(promos.filter(p => p.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete promo';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  const formatDate = (dateStr: string | null) =>
    dateStr ? formatAnyDateDisplay(dateStr, { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date';

  // Get status badges for promo card
  const getStatusBadges = (promo: Promo) => {
    const badges = [];
    
    // Website display badges (can have multiple)
    if (promo.display_type) {
      const displayTypes = promo.display_type.split(',').map(t => t.trim()).filter(t => t);
      const displayBadges: Record<string, { icon: typeof HiSpeakerphone; text: string; color: string }> = {
        banner: { icon: HiSpeakerphone, text: 'Website Banner', color: 'bg-amber-100 text-amber-700' },
        popup: { icon: HiSparkles, text: 'Website Popup', color: 'bg-pink-100 text-pink-700' },
        section: { icon: HiViewGrid, text: 'Website Section', color: 'bg-blue-100 text-blue-700' },
      };
      
      displayTypes.forEach(type => {
        const badge = displayBadges[type];
        if (badge) {
          const Icon = badge.icon;
          badges.push(
            <span key={`website-${type}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badge.color}`}>
              <Icon className="w-3 h-3" />
              {badge.text}
            </span>
          );
        }
      });
    }
    
    // Square discount badge (has promo code)
    if (promo.promo_code) {
      badges.push(
        <span 
          key="square" 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"
          title="This promo has a discount code that can be used in Square POS"
        >
          <HiCreditCard className="w-3 h-3" />
          Has Promo Code
        </span>
      );
    }
    
    // Synced to Square badge (actually synced)
    if (promo.square_discount_id) {
      badges.push(
        <span 
          key="synced" 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700"
          title="This discount has been successfully synced to Square POS and is available for staff to use"
        >
          <HiCheckCircle className="w-3 h-3" />
          Synced to Square
        </span>
      );
    } else if (promo.promo_code) {
      // Has code but not synced yet (sync may have failed or is pending)
      badges.push(
        <span 
          key="not-synced" 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700"
          title="This promo has a code but hasn't synced to Square yet. Sync happens automatically when saved. If this persists, check Square connection."
        >
          <HiCreditCard className="w-3 h-3" />
          Not Synced
        </span>
      );
    }
    
    return badges;
  };

  return (
    <div className="space-y-6">
      {/* Help Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <HiSparkles className="w-5 h-5" />
          How This Works
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p><strong>Toggle (ON/OFF):</strong> Controls if the promo is active. When OFF, it won't display anywhere.</p>
          <p><strong>Display Type:</strong> Choose how it shows on the website - <strong>Banner</strong> (top bar), <strong>Popup</strong> (modal), or <strong>Section</strong> (homepage). <strong>You can select multiple locations!</strong> If no display type is set, the promo won't show even if toggled ON.</p>
          <p><strong>Square Sync:</strong> Promos with a promo code automatically sync to Square POS. The "Synced to Square" badge appears when sync is complete.</p>
          <p><strong>Normal workflow:</strong> Create discounts here first — they push to Square. You almost never need the optional import under <em>Advanced</em> below.</p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discounts & Promotions</h1>
          <p className="text-gray-500 mt-1">
            Manage website banners, popups, and Square POS discount codes
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={fetchPromos}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Refresh"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openAnnouncementModal}
            className="inline-flex items-center gap-2 px-3 py-2 bg-amber-100 
                       text-amber-700 rounded-lg hover:bg-amber-200 text-sm"
          >
            <HiSpeakerphone className="w-4 h-4" />
            New Banner
          </button>
          <button
            onClick={openDailySpecialModal}
            className="inline-flex items-center gap-2 px-3 py-2 bg-pink-100 
                       text-pink-700 rounded-lg hover:bg-pink-200 text-sm"
          >
            <HiTag className="w-4 h-4" />
            Daily Special
          </button>
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 
                       text-white rounded-lg hover:bg-purple-700"
          >
            <HiPlus className="w-5 h-5" />
            New Discount
          </button>
        </div>
      </div>


      {/* Quick Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>💡 Tip:</strong> Discounts can serve multiple purposes:
          <br />
          • <strong>Website Display</strong> (Banner/Popup/Section) - Shows to customers on your website
          <br />
          • <strong>Square Discount Code</strong> - Staff can apply at POS checkout (auto-syncs when you add a promo code)
          <br />
          • <strong>Both</strong> - A discount can appear on website AND sync to Square
        </p>
      </div>

      {/*
        Square→portal import is tucked under <details> so the default UI matches
        "create in portal first". It only adds missing rows in our DB; it does not
        delete or edit discounts in Square (see syncDiscountsFromSquare in backend).
      */}
      <details className="rounded-lg border border-gray-200 bg-gray-50/80 text-sm text-gray-700">
        <summary className="cursor-pointer select-none px-4 py-3 font-medium text-gray-800 hover:bg-gray-100 rounded-lg">
          Advanced — import Square-only discount codes (rare)
        </summary>
        <div className="space-y-3 border-t border-gray-200 px-4 py-3 text-gray-600">
          <p>
            Use this only when a discount already exists in Square but was never added here
            (legacy codes or one-off POS entries). It <strong>reads</strong> Square and{' '}
            <strong>adds</strong> matching rows to this list — it does <strong>not</strong>{' '}
            change or remove anything in Square.
          </p>
          <button
            type="button"
            onClick={syncFromSquare}
            disabled={syncingFromSquare}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            title="Add Square catalog discounts to this list if missing"
          >
            <HiDownload className={`h-4 w-4 ${syncingFromSquare ? 'animate-spin' : ''}`} />
            {syncingFromSquare ? 'Importing…' : 'Import from Square'}
          </button>
        </div>
      </details>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading discounts...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && promos.length === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-lg font-semibold text-gray-900">No Discounts Yet</h3>
          <p className="text-gray-500 mt-2 mb-4">
            Create banners, specials, or discount codes here — codes with a promo code sync to Square automatically.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={openAnnouncementModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 
                         text-amber-700 rounded-lg hover:bg-amber-200"
            >
              <HiSpeakerphone className="w-5 h-5" />
              Create Announcement
            </button>
            <button
              onClick={openDailySpecialModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 
                         text-pink-700 rounded-lg hover:bg-pink-200"
            >
              <HiTag className="w-5 h-5" />
              Create Daily Special
            </button>
          </div>
        </div>
      )}

      {/* Promos List */}
      {!loading && promos.length > 0 && (
        <div className="space-y-4">
          {promos.map(promo => (
            <div
              key={promo.id}
              className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
                promo.is_active 
                  ? 'border-green-300 shadow-sm' 
                  : 'border-gray-200 opacity-60'
              }`}
            >
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Title row with badges */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900">{promo.title}</h3>
                      
                      {/* Daily special badge */}
                      {promo.is_daily_special && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">
                          ⏰ Daily Special
                        </span>
                      )}
                      
                      {/* Status badges (Website/Square/Synced) */}
                      {getStatusBadges(promo)}
                      
                      {/* Priority badge */}
                      {promo.priority && promo.priority < 50 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          Priority: {promo.priority}
                        </span>
                      )}
                    </div>
                    
                    {promo.description && (
                      <p className="text-gray-600 mb-2">{promo.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                      {promo.discount_type === 'percentage' && promo.discount_value && (
                        <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded">
                          {promo.discount_value}% off
                        </span>
                      )}
                      {promo.discount_type === 'fixed' && promo.discount_value && (
                        <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded">
                          ${promo.discount_value} off
                        </span>
                      )}
                      {promo.promo_code && (
                        <span className="bg-gray-100 px-2 py-1 rounded font-mono">
                          Code: {promo.promo_code}
                        </span>
                      )}
                      {promo.free_item && (
                        <span className="bg-green-50 text-green-700 px-2 py-1 rounded">
                          🎁 {promo.free_item}
                        </span>
                      )}
                      {promo.target_audience && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          For: {promo.target_audience}
                        </span>
                      )}
                      <span className="text-gray-400">
                        {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                      </span>
                    </div>
                    
                    {/* Usage tracking */}
                    {promo.promo_code && (
                      <div className="mt-2 text-sm text-gray-500">
                        📊 {promo.current_uses || 0} redemptions
                        {promo.max_total_uses && ` / ${promo.max_total_uses} max`}
                        {promo.max_uses_per_customer && promo.max_uses_per_customer > 1 && 
                          ` • ${promo.max_uses_per_customer} per customer`
                        }
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    {/* Big toggle switch with better tooltip */}
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => toggleActive(promo.id, promo.is_active)}
                        className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                          promo.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        title={
                          promo.is_active 
                            ? 'Active: Promo is enabled. Will display if display type is set (banner/popup/section).' 
                            : 'Inactive: Promo is disabled and will not display anywhere.'
                        }
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${
                            promo.is_active ? 'translate-x-9' : 'translate-x-1'
                          }`}
                        />
                        <span className={`absolute text-xs font-medium ${promo.is_active ? 'left-2 text-white' : 'right-2 text-gray-500'}`}>
                          {promo.is_active ? 'ON' : 'OFF'}
                        </span>
                      </button>
                      {/* Warning if active but no display type */}
                      {promo.is_active && (!promo.display_type || displayTypeToArray(promo.display_type).length === 0) && (
                        <span className="text-xs text-amber-600 font-medium" title="This promo is active but won't display because no display type is set. Edit to add display types (banner/popup/section).">
                          ⚠️ Not displaying
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => openEditModal(promo)}
                      className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg"
                      title="Edit"
                    >
                      <HiPencil className="w-5 h-5 text-purple-700" />
                    </button>
                    <button
                      onClick={() => deletePromo(promo.id, promo.title)}
                      className="p-2 bg-red-100 hover:bg-red-200 rounded-lg"
                      title="Delete"
                    >
                      <HiTrash className="w-5 h-5 text-red-700" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">
                {editingId ? 'Edit Discount' : form.is_daily_special ? 'New Daily Special' : 'New Discount'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-6">
              {/* Section 1: Website Display */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <HiGlobe className="w-4 h-4 text-blue-600" />
                  Website Display
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Choose where this should appear on your website. <strong>You can select multiple locations.</strong> If none selected, the promo won't display on the website (even if toggle is ON). Leave all unchecked for Square-only discounts.
                </p>
                
                <div className="space-y-2">
                  {[
                    { type: 'banner', icon: HiSpeakerphone, label: 'Banner', desc: 'Top bar', selectedClass: 'border-amber-500 bg-amber-50', iconClass: 'text-amber-600' },
                    { type: 'popup', icon: HiSparkles, label: 'Popup', desc: 'Modal window', selectedClass: 'border-pink-500 bg-pink-50', iconClass: 'text-pink-600' },
                    { type: 'section', icon: HiViewGrid, label: 'Section', desc: 'Homepage', selectedClass: 'border-blue-500 bg-blue-50', iconClass: 'text-blue-600' },
                  ].map(({ type, icon: Icon, label, desc, selectedClass, iconClass }) => {
                    const isSelected = displayTypeToArray(form.display_type).includes(type);
                    return (
                      <label
                        key={type}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          isSelected
                            ? selectedClass
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDisplayType(type)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <Icon className={`w-5 h-5 ${iconClass}`} />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{label}</div>
                          <div className="text-xs text-gray-500">{desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                
                {/* Show selected types */}
                {displayTypeToArray(form.display_type).length > 0 && (
                  <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                    Selected: {displayTypeToArray(form.display_type).join(', ')}
                  </div>
                )}
                
                {/* Clear all button */}
                {displayTypeToArray(form.display_type).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, display_type: '' })}
                    className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear all selections
                  </button>
                )}
              </div>

              {/* Section 2: Square Discount Code */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <HiCreditCard className="w-4 h-4 text-green-600" />
                  Square Discount Code (Optional)
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Add a promo code to sync this discount to Square POS. Staff can apply it at checkout.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promo Code
                  </label>
                  <input
                    type="text"
                    value={form.promo_code}
                    onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SAVE20"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {form.promo_code 
                      ? '✅ Will sync to Square POS automatically when saved'
                      : 'Leave empty if this is website-only (no Square sync)'}
                  </p>
                </div>
              </div>

              {/* Section 3: Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h3>
                
                {/* Daily Special Toggle */}
                <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg mb-4">
                  <input
                    type="checkbox"
                    id="is_daily_special"
                    checked={form.is_daily_special}
                    onChange={(e) => setForm({ ...form, is_daily_special: e.target.checked })}
                    className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <label htmlFor="is_daily_special" className="text-sm">
                    <span className="font-medium text-yellow-800">⏰ Daily Special</span>
                    <span className="text-yellow-600 ml-1">— Shows "TODAY ONLY" badge</span>
                  </label>
                </div>

                {/* Title */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Holiday Sale - 20% Off!"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    placeholder="Add more details..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Discount Type & Value */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offer Type
                    </label>
                    <select
                      value={form.discount_type}
                      onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="info">Info Only</option>
                      <option value="percentage">% Off</option>
                      <option value="fixed">$ Off</option>
                    </select>
                  </div>
                  {form.discount_type !== 'info' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {form.discount_type === 'percentage' ? 'Percentage' : 'Amount'}
                      </label>
                      <input
                        type="number"
                        value={form.discount_value}
                        onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                        placeholder={form.discount_type === 'percentage' ? '20' : '5.00'}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  )}
                </div>

                {/* Free Item */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Item (optional)
                  </label>
                  <input
                    type="text"
                    value={form.free_item}
                    onChange={(e) => setForm({ ...form, free_item: e.target.value })}
                    placeholder="e.g., Small Coffee, Cookie"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Priority */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (1 = highest)
                  </label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    placeholder="100"
                    min="1"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Link path — where banner/section CTA goes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link path (optional)
                  </label>
                  <input
                    type="text"
                    value={form.link_path}
                    onChange={(e) => setForm({ ...form, link_path: e.target.value })}
                    placeholder="/menu/thanksgiving-2026 or /menu?category=Stuffed%20Cookies"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Site path for the banner/card button. Leave blank for text-only banners.
                  </p>
                </div>
              </div>

              {/* Advanced Section - Collapsible */}
              <details className="border rounded-lg">
                <summary className="px-4 py-2 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Advanced Options (Coupon Tracking)
                </summary>
                <div className="p-4 space-y-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Audience
                    </label>
                    <input
                      type="text"
                      value={form.target_audience}
                      onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                      placeholder="e.g., Email subscribers, Local customers"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Total Uses
                      </label>
                      <input
                        type="number"
                        value={form.max_total_uses}
                        onChange={(e) => setForm({ ...form, max_total_uses: e.target.value })}
                        placeholder="Unlimited"
                        min="1"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Uses Per Customer
                      </label>
                      <input
                        type="number"
                        value={form.max_uses_per_customer}
                        onChange={(e) => setForm({ ...form, max_uses_per_customer: e.target.value })}
                        placeholder="1"
                        min="1"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="requires_verification"
                      checked={form.requires_verification}
                      onChange={(e) => setForm({ ...form, requires_verification: e.target.checked })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="requires_verification" className="text-sm text-gray-700">
                      Require staff verification before redeeming
                    </label>
                  </div>
                </div>
              </details>

              {/* Preview */}
              {form.display_type === 'banner' && form.title && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="text-xs text-gray-500 px-3 py-1 bg-gray-100">Preview</div>
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4">
                    <p className="text-sm text-center">
                      {form.is_daily_special && (
                        <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs mr-2">TODAY ONLY</span>
                      )}
                      <span className="font-bold">{form.title}</span>
                      {form.discount_type === 'percentage' && form.discount_value && (
                        <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                          {form.discount_value}% OFF
                        </span>
                      )}
                      {form.free_item && <span className="ml-2">🎁 Free {form.free_item}</span>}
                      {form.promo_code && (
                        <span className="ml-2 bg-white/30 px-2 py-0.5 rounded font-mono text-xs">
                          {form.promo_code}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg 
                           hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? 'Saving...' : (
                  <>
                    <HiCheck className="w-5 h-5" />
                    {editingId ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
